import type { CdkDragDrop, CdkDragStart } from '@angular/cdk/drag-drop';
import {
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { SlicePipe } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  input,
  output,
  Renderer2,
  RendererStyleFlags2,
  signal,
  ViewChild,
} from '@angular/core';

import { DisqualifyReasonsDropdownTemplateComponent } from '@recruitee/disqualify-reasons';
import type { DisqualifyReason } from '@recruitee/disqualify-reasons-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { HiddenResultPreview, InterviewResult } from '@recruitee/interview-results-types';
import type { Location } from '@recruitee/locations-types';
import type { Placement } from '@recruitee/placements-types';
import type { Stage } from '@recruitee/stages-types';
import {
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconComponent,
  NestedTemplateTriggerDirective,
} from '@recruitee/user-interface';
import { deepClone, groupByKey } from '@recruitee/utils';

import { CandidateDisqualifiedTooltipComponent } from '../candidate-disqualified-tooltip/candidate-disqualified-tooltip.component';
import { CandidateOfferTooltipComponent } from '../candidate-offer-tooltip/candidate-offer-tooltip.component';
import { CandidateProfilePlacementComponent } from '../candidate-profile-placement/candidate-profile-placement.component';
import { MorePlacementsToggleComponent } from './more-placements-toggle/more-placements-toggle.component';
import type {
  DisqualifyEvent,
  PlacementActionsCollection,
  PlacementEvent,
  PlacementOpeningIssueCollection,
  ProceedEvent,
  ShowAutomatedActionDetailsEvent,
  UpdatePlacementPositionsEvent,
} from './types';
import { getNextStage } from './utils';

@Component({
  selector: 'rt-candidate-profile-placements',
  templateUrl: './candidate-profile-placements.component.html',
  styleUrls: ['./candidate-profile-placements.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CandidateProfilePlacementComponent,
    DropdownComponent,
    DropdownItemComponent,
    IconComponent,
    TranslatePipe,
    MorePlacementsToggleComponent,
    SlicePipe,
    CdkDrag,
    CdkDropList,
    CdkDragHandle,
    CdkDragPlaceholder,
    NestedTemplateTriggerDirective,
    DisqualifyReasonsDropdownTemplateComponent,
    CandidateOfferTooltipComponent,
    CandidateDisqualifiedTooltipComponent,
  ],
  host: {
    '[class.has-show-more-toggle]': 'hasShowMoreToggle()',
  },
})
export class CandidateProfilePlacementsComponent {
  public readonly DropdownWidth = DropdownWidth;
  public readonly placementLimit = 4;

  public placements = input.required<Placement[]>();

  public stages = input.required<Stage[]>();

  public placementActions = input.required<PlacementActionsCollection>();

  public canManageCandidates = input.required<boolean>();

  public disqualifyReasons = input.required<DisqualifyReason[]>();

  public locations = input.required<Location[]>();

  public loadingPlacementIds = input<number[]>([]);

  public showDragHandles = input(false, { transform: booleanAttribute });

  public placementOpeningIssues = input<PlacementOpeningIssueCollection>({});

  public interviewResults = input.required<Record<number, InterviewResult[]>>();

  public hiddenResultPreviews = input.required<
    Record<number, HiddenResultPreview[]>,
    HiddenResultPreview[]
  >({
    transform: (previews: HiddenResultPreview[]) => groupByKey(previews, 'offerId'),
  });

  public showMoreToggleValue = signal(false);

  /**
   * Key is placement id, value is array of associated stages.
   */
  public placementStages = computed(() =>
    this.placements().reduce(
      (acc, placement) => ({
        ...acc,
        [placement.id]: this.stages()
          .filter(stage => stage.pipelineTemplateId === placement.offer?.pipelineTemplateId)
          .sort((a, b) => a.position - b.position),
      }),
      <Record<number, Stage[]>>{},
    ),
  );

  public hasShowMoreToggle = computed(() => this.placements().length > this.placementLimit);

  public hasMultiplePlacements = computed(() => this.placements().length > 1);

  public remainingPlacementsCount = computed(() => {
    if (this.showMoreToggleValue()) {
      return 0;
    }

    return this.placements().length - this.placementLimit;
  });

  public showCount = computed(() => this.placements().length - this.remainingPlacementsCount());

  public placementNextStages = computed(() =>
    this.placements().reduce(
      (acc, placement) => ({
        ...acc,
        [placement.id]: getNextStage(placement, this.placementStages()[placement.id]),
      }),
      <Record<number, Stage>>{},
    ),
  );

  public placementOfferLocationNames = computed(() =>
    this.placements().reduce(
      (acc, placement) => {
        const locationIds = placement.offer?.locationIds || [];
        const locations = this.locations().filter(location => locationIds.includes(location.id));
        const locationNames =
          locations
            .map(location =>
              location.archivedAt
                ? `${location.name} (${this.i18n
                    .translate('app.locations.archived')
                    .toLowerCase()})`
                : location.name,
            )
            .join(', ') || placement.offer!.location;
        return { ...acc, [placement.id]: locationNames };
      },
      <Record<number, string>>{},
    ),
  );

  public hasPositiveOrHiddenRatings = computed(() =>
    this.placements().some(
      placement => placement.positiveRatings !== null || !placement.ratingVisible,
    ),
  );

  public viewPipeline = output<PlacementEvent>();

  public remove = output<PlacementEvent>();

  public editHiringDetails = output<PlacementEvent>();

  public updatePlacementPositions = output<UpdatePlacementPositionsEvent>();

  public proceed = output<ProceedEvent>();

  public disqualify = output<DisqualifyEvent>();

  public requalify = output<PlacementEvent>();

  public showAutomatedActionDetails = output<ShowAutomatedActionDetailsEvent>();

  public editDisqualifyReasons = output<void>();

  public editWorkLocation = output<PlacementEvent>();

  public addToJob = output<void>();

  public disqualifyReasonActionsDetails = output<DisqualifyReason>();

  public openEvaluationTab = output<void>();

  @ViewChild('customPlaceholder', { read: ElementRef })
  private customPlaceholder: ElementRef;

  constructor(
    private renderer: Renderer2,
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public onDrop($event: CdkDragDrop<Placement>) {
    const { previousIndex, currentIndex } = $event;
    const newPlacements = deepClone(this.placements());

    moveItemInArray(newPlacements, previousIndex, currentIndex);
    this.updatePlacementPositions.emit({
      placement: newPlacements[currentIndex],
      positions: newPlacements.map((item, index) => ({
        id: item.id,
        position: index + 1,
      })),
    });
  }

  public onDragStart($event: CdkDragStart) {
    this.cdRef.detectChanges();
    const draggedElementHeight = $event.source.element.nativeElement.offsetHeight;
    this.renderer.setStyle(
      this.customPlaceholder.nativeElement,
      'height',
      `${draggedElementHeight}px`,
    );
    this.renderer.setStyle(document.body, 'cursor', 'grabbing', RendererStyleFlags2.Important);
  }

  public onDragEnded() {
    this.renderer.removeStyle(document.body, 'cursor');
  }

  public preventCloseOnBackAction($event: MouseEvent): void {
    $event.preventDefault();
  }
}
